import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrderResponse } from "../../services/Customer/CustomerOrderList/customerOrderListAPI.types";
import { upsertOrderGroup, initialState, upsertOrderGroups, resetList } from "../model/order";
import { updatePeriod } from "../period";
import { setBoot } from "../boot";
import {
  resetFilter,
  updateFilterMenuByPeriod,
  updateFilterOption,
  updateFilterOptions,
  updateSearchKeyword,
  updateSelectedFilter,
} from "./filter";
import { resetSort, updateSortOption } from "./sort";

const { reducer, actions } = createSlice({
  name: "orders",
  initialState,
  reducers: {
    updateOrdersList: (state, { payload }: PayloadAction<{ requestId: string; data: OrderResponse }>) => {
      const { requestId, data } = payload;

      if (state.requestId === requestId) {
        const { Period, CustomerOrderList, ShowCSIInfo } = data;

        // note: allOrderCount is used to sum all the order counts from each bonus period
        const { orderGroups, allOrderCount } = state;
        const orderGroup = upsertOrderGroup(data, orderGroups);
        const _orderGroups = upsertOrderGroups(Period, orderGroups, orderGroup);

        // showCSIInfo is derived from ShowCSIInfo of the first response (selected/starting month)
        const showCSIInfo = orderGroups.length === 0 ? (ShowCSIInfo ?? false) : state.showCSIInfo;

        return {
          ...state,
          ...data,
          showCSIInfo,
          orderGroups: _orderGroups,
          allOrderCount: allOrderCount + CustomerOrderList?.length,
          isSuccess: true,
          isError: false,
          isFetching: false,
        };
      }
      return state;
    },
    updateIsFetching: (state, { payload }: PayloadAction<{ requestId: string; isFetching: boolean }>) => ({
      ...state,
      ...payload,
      isError: false,
      isSuccess: false,
    }),
    updateIsError: (state, { payload }: PayloadAction<{ requestId: string; isError: boolean }>) => {
      const { requestId, isError } = payload;

      if (state.requestId === requestId) {
        return {
          ...state,
          isError,
          isSuccess: isError === false,
          isFetching: false,
        };
      }

      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updatePeriod, (state) => ({
      ...resetList(),
      showCSIInfo: state.showCSIInfo,
    }));
    builder.addCase(setBoot, () => resetList());
    builder.addCase(resetFilter, (state) => ({
      ...resetList(),
      showCSIInfo: state.showCSIInfo,
    }));
    builder.addCase(updateFilterOptions, (state) => ({
      ...resetList(),
      showCSIInfo: state.showCSIInfo,
    }));
    builder.addCase(updateFilterOption, (state) => ({
      ...resetList(),
      showCSIInfo: state.showCSIInfo,
    }));
    builder.addCase(updateSearchKeyword, (state) => ({
      ...resetList(),
      showCSIInfo: state.showCSIInfo,
    }));
    builder.addCase(updateSortOption, (state) => ({
      ...resetList(),
      showCSIInfo: state.showCSIInfo,
    }));
    builder.addCase(resetSort, (state) => ({
      ...resetList(),
      showCSIInfo: state.showCSIInfo,
    }));
    builder.addCase(updateSelectedFilter, (state) => ({
      ...resetList(),
      showCSIInfo: state.showCSIInfo,
    }));
  },
});

export const { updateOrdersList, updateIsFetching, updateIsError } = actions;

export default reducer;
